import {
  type DnbResponse,
  type publishDocControllerPostGuests,
  type PublishDocControllerPostGuestsParams,
  type publishDocControllerPostPublishGuestDoc,
  type PublishDocControllerPostPublishGuestDocBody,
  type publishDocControllerPostRevokePublicGuestDoc,
  type RevokeGuestDocDto,
  usePublishDocControllerPostGuests,
  usePublishDocControllerPostPublishGuestDoc,
  usePublishDocControllerPostRevokePublicGuestDoc,
} from '@docnboard/rest';
import {
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

export const useGuests = <
  TData = Awaited<ReturnType<typeof publishDocControllerPostGuests>>,
  TError = unknown,
>(
  workspaceId: string,
  docId: string,
  params?: PublishDocControllerPostGuestsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publishDocControllerPostGuests>>,
        TError,
        TData
      >
    >;
  }
) => {
  const queryKey = [
    'permission',
    'guest',
    workspaceId,
    docId,
    ...(params ? [params] : []),
  ] as const;

  return usePublishDocControllerPostGuests(workspaceId, docId, params, {
    query: {
      ...options?.query,
      queryKey,
    },
  });
};

export const useInviteGuest = <
  TError = DnbResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof publishDocControllerPostPublishGuestDoc>>,
    TError,
    {
      workspaceId: string;
      docId: string;
      data: PublishDocControllerPostPublishGuestDocBody;
    },
    TContext
  >;
}): UseMutationResult<
  Awaited<ReturnType<typeof publishDocControllerPostPublishGuestDoc>>,
  TError,
  {
    workspaceId: string;
    docId: string;
    data: PublishDocControllerPostPublishGuestDocBody;
  },
  TContext
> => {
  const queryClient = useQueryClient();

  return usePublishDocControllerPostPublishGuestDoc({
    mutation: {
      ...options?.mutation,
      onSuccess: async (variables, context, data) => {
        options?.mutation?.onSuccess?.(variables, context, data);

        await queryClient.invalidateQueries({
          queryKey: ['permission', 'guest'],
        });
      },
    },
  });
};

export const useRevokeGuest = <
  TError = DnbResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof publishDocControllerPostRevokePublicGuestDoc>>,
    TError,
    { workspaceId: string; docId: string; data: RevokeGuestDocDto },
    TContext
  >;
}): UseMutationResult<
  Awaited<ReturnType<typeof publishDocControllerPostRevokePublicGuestDoc>>,
  TError,
  { workspaceId: string; docId: string; data: RevokeGuestDocDto },
  TContext
> => {
  const queryClient = useQueryClient();

  return usePublishDocControllerPostRevokePublicGuestDoc({
    mutation: {
      ...options?.mutation,
      onSuccess: async (variables, context, data) => {
        options?.mutation?.onSuccess?.(variables, context, data);

        await queryClient.invalidateQueries({
          queryKey: ['permission', 'guest'],
        });
      },
    },
  });
};
